import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, RotateCcw, Check, Play, Pause, SwitchCamera, Video } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useHaptics } from '@/hooks/useHaptics';
import { MAX_VIDEO_DURATION } from '@/utils/videoValidation';
import { pickSupportedRecordingFormat } from '@/utils/mediaRecorder';

interface InlineVideoRecorderProps {
  onVideoRecorded: (file: File, duration: number) => void;
  onCancel: () => void;
  disabled?: boolean;
}

export function InlineVideoRecorder({ onVideoRecorded, onCancel, disabled }: InlineVideoRecorderProps) {
  const [cameraState, setCameraState] = useState<'loading' | 'active' | 'previewing' | 'error'>('loading');
  const [isRecording, setIsRecording] = useState(false);
  const [recordedDuration, setRecordedDuration] = useState(0);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [isPlaying, setIsPlaying] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recordedBlobRef = useRef<Blob | null>(null);
  const recordingFormatRef = useRef<ReturnType<typeof pickSupportedRecordingFormat> | null>(null);
  const previewObjectUrlRef = useRef<string | null>(null);
  const recordingIntervalRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const accumulatedTimeRef = useRef<number>(0);
  
  const haptics = useHaptics();

  // Stop camera stream
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  // Start camera stream
  const startCamera = useCallback(async (facing: 'user' | 'environment' = facingMode) => {
    setCameraState('loading');
    setErrorMessage('');
    stopCamera();

    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera not supported on this device');
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facing,
          width: { ideal: 1080 },
          height: { ideal: 1440 }, // 3:4 aspect ratio
        },
        audio: true,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setCameraState('active');
      }
    } catch (error) {
      console.error('Camera error:', error);
      const err = error as Error;
      
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setErrorMessage('Camera access denied. Please allow camera and microphone access.');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setErrorMessage('No camera found on this device.');
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        setErrorMessage('Camera is in use by another app.');
      } else if (err.name === 'OverconstrainedError') {
        if (facing === 'environment') {
          setFacingMode('user');
          startCamera('user');
          return;
        }
        setErrorMessage(err.message || 'Could not access camera');
      } else {
        setErrorMessage(err.message || 'Could not access camera');
      }
      
      setCameraState('error');
    }
  }, [facingMode, stopCamera]);

  // Initialize camera on mount
  useEffect(() => {
    startCamera();
    
    return () => {
      stopCamera();
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }

      // Cleanup preview URL
      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current);
        previewObjectUrlRef.current = null;
      }
    };
  }, []);

  const setPreviewFromBlob = useCallback((blob: Blob) => {
    recordedBlobRef.current = blob;

    // Revoke previous object URL to avoid leaks
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
    }

    const url = URL.createObjectURL(blob);
    previewObjectUrlRef.current = url;

    // IMPORTANT: set src + load/play on the already-mounted element
    // (the element will be "primed" by a play() call inside the user gesture).
    if (previewVideoRef.current) {
      previewVideoRef.current.src = url;
      previewVideoRef.current.currentTime = 0;
      previewVideoRef.current.load();
      previewVideoRef.current.play().catch(() => {
        // Autoplay may be blocked; user can hit play.
      });
    }
  }, []);

  // Flip camera
  const flipCamera = useCallback(() => {
    const newFacing = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newFacing);
    startCamera(newFacing);
    haptics.light();
  }, [facingMode, startCamera, haptics]);

  // Start recording segment
  const startRecordingSegment = useCallback(() => {
    if (!streamRef.current || recordedDuration >= MAX_VIDEO_DURATION || disabled) return;

    haptics.medium();
    setIsRecording(true);
    startTimeRef.current = Date.now();

    // Only create new MediaRecorder if this is first segment
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
      if (typeof MediaRecorder === 'undefined') {
        toast.error('Video recording is not supported on this device');
        return;
      }

      const fmt = pickSupportedRecordingFormat();
      recordingFormatRef.current = fmt;

      let mediaRecorder: MediaRecorder;
      try {
        mediaRecorder = new MediaRecorder(streamRef.current, {
          mimeType: fmt.mimeType,
        });
      } catch (err) {
        // Some browsers throw even if isTypeSupported lied; fall back to default constructor.
        console.warn('MediaRecorder init failed with mimeType, falling back:', err);
        mediaRecorder = new MediaRecorder(streamRef.current);
        recordingFormatRef.current = { mimeType: 'video/webm', blobType: 'video/webm', extension: 'webm' };
      }

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        if (chunksRef.current.length > 0) {
          const blobType = recordingFormatRef.current?.blobType ?? 'video/webm';
          recordedBlobRef.current = new Blob(chunksRef.current, { type: blobType });
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      mediaRecorder.start();
    } else if (mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
    }

    // Update duration counter
    recordingIntervalRef.current = window.setInterval(() => {
      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      const newDuration = accumulatedTimeRef.current + elapsed;
      
      if (newDuration >= MAX_VIDEO_DURATION) {
        stopRecordingSegment();
        finishRecording();
      } else {
        setRecordedDuration(newDuration);
      }
    }, 50);
  }, [recordedDuration, haptics, disabled]);

  // Stop recording segment (pause)
  const stopRecordingSegment = useCallback(() => {
    if (!isRecording) return;

    haptics.light();
    setIsRecording(false);
    
    // Update accumulated time
    const elapsed = (Date.now() - startTimeRef.current) / 1000;
    accumulatedTimeRef.current += elapsed;
    setRecordedDuration(accumulatedTimeRef.current);

    // Clear interval
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }

    // Pause MediaRecorder
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
    }
  }, [isRecording, haptics]);

  // Finish recording and show preview
  const finishRecording = useCallback(() => {
    if (recordedDuration < 1) {
      toast.error('Record at least 1 second');
      return;
    }

    // Switch UI to preview mode immediately within the user gesture.
    // This ensures the preview <video> element is present and can be "primed".
    setCameraState('previewing');
    requestAnimationFrame(() => {
      // "Prime" the element in the user-gesture path; on iOS this helps prevent black preview.
      previewVideoRef.current?.play().catch(() => {});
    });

    // Stop camera to free resources
    stopCamera();

    // If MediaRecorder is active, stop it and wait for onstop to fire
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      // Override onstop to immediately show preview when blob is ready
      mediaRecorderRef.current.onstop = () => {
        if (chunksRef.current.length > 0) {
          const blobType = recordingFormatRef.current?.blobType ?? 'video/webm';
          const blob = new Blob(chunksRef.current, { type: blobType });
          setPreviewFromBlob(blob);
        }
      };
      mediaRecorderRef.current.stop();
    } else if (recordedBlobRef.current) {
      // Blob already exists (from previous pause), show it directly
      setPreviewFromBlob(recordedBlobRef.current);
    }
  }, [recordedDuration, stopCamera, setPreviewFromBlob]);

  // Reset recording
  const resetRecording = useCallback(() => {
    setIsRecording(false);
    setCameraState('loading');
    setRecordedDuration(0);
    setIsPlaying(false);
    accumulatedTimeRef.current = 0;
    chunksRef.current = [];
    recordedBlobRef.current = null;

    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = null;
    }
    
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
    }
    
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    mediaRecorderRef.current = null;

    // Restart camera
    startCamera();
  }, [startCamera]);

  // Confirm and use recording
  const confirmRecording = useCallback(() => {
    if (!recordedBlobRef.current) return;

    const ext = recordingFormatRef.current?.extension ?? 'webm';
    const type = recordedBlobRef.current.type || (ext === 'mp4' ? 'video/mp4' : 'video/webm');

    const file = new File([recordedBlobRef.current], `video_${Date.now()}.${ext}`, {
      type,
    });
    
    onVideoRecorded(file, recordedDuration);
    haptics.success();
  }, [recordedDuration, onVideoRecorded, haptics]);

  // Toggle preview playback
  const togglePlayback = useCallback(() => {
    if (!previewVideoRef.current) return;
    
    if (isPlaying) {
      previewVideoRef.current.pause();
    } else {
      previewVideoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  const progressPercent = (recordedDuration / MAX_VIDEO_DURATION) * 100;

  return (
    <div className="relative w-full">
      {/* Camera container with 3:4 aspect ratio */}
      <div className="relative aspect-[3/4] w-full rounded-2xl overflow-hidden bg-black">
        {/* Progress Bar - more prominent */}
        <div className="absolute top-0 left-0 right-0 h-2 bg-black/40 z-10">
          <motion.div
            className="h-full bg-gradient-to-r from-red-500 to-red-400"
            style={{ width: `${progressPercent}%` }}
            animate={{ width: `${progressPercent}%` }}
            transition={{ duration: 0.05 }}
          />
          {/* Progress markers */}
          <div className="absolute inset-0 flex justify-between px-1">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="w-px h-full bg-white/30" />
            ))}
          </div>
        </div>

        {/* Duration Display - larger and more visible */}
        <div className="absolute top-5 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-black/60 backdrop-blur-sm z-10 border border-white/20">
          <span className="text-white font-mono text-base font-medium">
            {recordedDuration.toFixed(1)}s / {MAX_VIDEO_DURATION}s
          </span>
        </div>

        {/* Cancel Button */}
        <button
          onClick={onCancel}
          className="absolute top-4 left-4 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center z-10"
        >
          <X className="w-5 h-5 text-white" />
        </button>

        {/* Live Camera Feed */}
        {cameraState !== 'previewing' && (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={cn(
              "absolute inset-0 w-full h-full object-cover",
              facingMode === 'user' && "scale-x-[-1]",
              cameraState !== 'active' && "opacity-0"
            )}
          />
        )}

        {/* Preview Video */}
        {cameraState === 'previewing' && (
          <video
            ref={previewVideoRef}
            playsInline
            loop
            muted
            autoPlay
            className="absolute inset-0 w-full h-full object-cover"
            onLoadedData={() => {
              // Ensure video plays on load (mobile requires muted + user gesture context)
              previewVideoRef.current?.play().catch(() => {});
              setIsPlaying(true);
            }}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
          />
        )}

        {/* Loading state */}
        <AnimatePresence>
          {cameraState === 'loading' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 flex flex-col items-center justify-center bg-muted/50"
            >
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mb-3" />
              <p className="text-sm text-muted-foreground">Starting camera...</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Error state */}
        <AnimatePresence>
          {cameraState === 'error' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 flex flex-col items-center justify-center bg-muted p-6 text-center"
            >
              <div className="w-16 h-16 rounded-full bg-muted-foreground/10 flex items-center justify-center mb-4">
                <Video className="w-8 h-8 text-muted-foreground" />
              </div>
              <p className="text-sm text-muted-foreground mb-4">{errorMessage}</p>
              <button 
                onClick={() => startCamera()}
                className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium"
              >
                Try Again
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Recording Indicator */}
        {isRecording && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500 z-10"
          >
            <motion.div
              className="w-2 h-2 rounded-full bg-white"
              animate={{ opacity: [1, 0.3, 1] }}
              transition={{ duration: 0.8, repeat: Infinity }}
            />
            <span className="text-white text-xs font-medium">REC</span>
          </motion.div>
        )}

        {/* Flip Camera Button (only during active camera) */}
        {cameraState === 'active' && !isRecording && (
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9, rotate: 180 }}
            onClick={flipCamera}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center z-10"
          >
            <SwitchCamera className="w-5 h-5 text-white" />
          </motion.button>
        )}

        {/* Bottom Controls */}
        <div className="absolute bottom-0 left-0 right-0 p-6 pb-10 bg-gradient-to-t from-black/60 to-transparent">
          <div className="flex flex-col items-center">
            {cameraState === 'previewing' ? (
              /* Preview Controls */
              <div className="flex items-center justify-center gap-6">
                {/* Retake */}
                <button
                  onClick={resetRecording}
                  className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center"
                >
                  <RotateCcw className="w-5 h-5 text-white" />
                </button>

                {/* Play/Pause */}
                <button
                  onClick={togglePlayback}
                  className="w-16 h-16 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center border-4 border-white"
                >
                  {isPlaying ? (
                    <Pause className="w-6 h-6 text-white" />
                  ) : (
                    <Play className="w-6 h-6 text-white ml-1" />
                  )}
                </button>

                {/* Use Video */}
                <button
                  onClick={confirmRecording}
                  className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center"
                >
                  <Check className="w-5 h-5 text-white" />
                </button>
              </div>
            ) : cameraState === 'active' ? (
              /* Recording Controls */
              <div className="flex flex-col items-center gap-3">
                <div className="flex items-center gap-5">
                  {/* Reset button - only show when we have recorded content and not currently recording */}
                  {recordedDuration > 0 && !isRecording && (
                    <button
                      onClick={resetRecording}
                      className="w-12 h-12 rounded-full bg-white/25 backdrop-blur-sm flex items-center justify-center"
                    >
                      <RotateCcw className="w-5 h-5 text-white" />
                    </button>
                  )}

                  {/* Record / Pause Button - tap to toggle */}
                  <button
                    onClick={() => {
                      if (isRecording) {
                        stopRecordingSegment();
                      } else {
                        startRecordingSegment();
                      }
                    }}
                  >
                    {/* Outer ring */}
                    <div className={cn(
                      "w-20 h-20 rounded-full border-[5px] flex items-center justify-center transition-colors duration-150",
                      isRecording 
                        ? "border-red-500" 
                        : "border-white"
                    )}>
                      {/* Inner button - animates between circle (start) and square (pause) */}
                      <motion.div
                        className={cn(
                          "rounded-full",
                          isRecording ? "bg-red-500" : "bg-white"
                        )}
                        animate={{
                          width: isRecording ? 28 : 64,
                          height: isRecording ? 28 : 64,
                          borderRadius: isRecording ? 6 : 32,
                        }}
                        transition={{ duration: 0.15 }}
                      />
                    </div>
                  </button>

                  {/* Done button - only show when paused with recorded content */}
                  {recordedDuration > 0 && !isRecording && (
                    <button
                      onClick={finishRecording}
                      className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center"
                    >
                      <Check className="w-5 h-5 text-white" />
                    </button>
                  )}
                </div>

                {/* Hints */}
                {recordedDuration === 0 && !isRecording ? (
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-white/80 text-sm font-medium"
                  >
                    Tap to start recording
                  </motion.p>
                ) : recordedDuration > 0 && !isRecording ? (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-white/60 text-xs"
                  >
                    Tap to resume · ✓ to finish
                  </motion.p>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
